import React from "react";
import { useParams } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { API } from "../data/API/API";
import InternalCommentsChatDock from "../components/InternalComments/InternalCommentsChatDock";
import "./NullReportingReviewPage.scss";

type StepStatus = "active" | "complete" | "pending";

const STATUS_DRAFT = 0;
const STATUS_ONGOING = 1;
const STATUS_REPLIED = 2;
const STATUS_HANDLED = 3;

const stepMeta = [
  { step: 1, label: "Kontaktförsök" },
  { step: 2, label: "Svar mottaget" },
  { step: 3, label: "Slutmarkerad" },
];

function getStepState(status: number): { s1: StepStatus; s2: StepStatus; s3: StepStatus } {
  if (status === STATUS_DRAFT) return { s1: "active", s2: "pending", s3: "pending" };
  if (status === STATUS_ONGOING || status === STATUS_REPLIED)
    return { s1: "complete", s2: status === STATUS_REPLIED ? "complete" : "active", s3: "pending" };
  return { s1: "complete", s2: "complete", s3: "active" };
}

const Stepper: React.FC<{ status: number }> = ({ status }) => {
  const st = getStepState(status);
  const states: StepStatus[] = [st.s1, st.s2, st.s3];

  return (
    <div className="nr-review-stepper">
      {stepMeta.map((m, idx) => {
        const s = states[idx];
        return (
          <React.Fragment key={m.step}>
            <div className="nr-review-step">
              <div className={`nr-review-step-circle ${s}`}>
                {s === "complete" ? <FontAwesomeIcon icon={faCheck} /> : m.step}
              </div>
              <div className={`nr-review-step-label ${s}`}>{m.label}</div>
            </div>
            {idx < 2 && <div className="nr-review-step-line" />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const toDateTimeLocalNow = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const getOutcomeBadge = (outcome?: number | null) => {
  switch (outcome) {
    case 1:
      return <span className="badge bg-secondary">Inget svar</span>;
    case 2:
      return <span className="badge bg-warning text-dark">Upptaget</span>;
    case 3:
      return <span className="badge bg-success">Svar erhållet</span>;
    default:
      return null;
  }
};

const statusLabel = (status: number) => {
  switch (status) {
    case STATUS_DRAFT:
      return "Utkast";
    case STATUS_ONGOING:
      return "Pågående";
    case STATUS_REPLIED:
      return "Svar mottaget";
    case STATUS_HANDLED:
      return "Slutförd";
    default:
      return `Okänd (${status})`;
  }
};

const FollowUpPhoneReviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = React.useState(true);
  const [details, setDetails] = React.useState<any | null>(null);

  const [supplierReply, setSupplierReply] = React.useState("");
  const [handledComment, setHandledComment] = React.useState("");

  const [newAttemptContactName, setNewAttemptContactName] = React.useState("");
  const [newAttemptAt, setNewAttemptAt] = React.useState(() => toDateTimeLocalNow());
  const [newAttemptNotes, setNewAttemptNotes] = React.useState("");
  const [newAttemptOutcome, setNewAttemptOutcome] = React.useState<number | "">("");

  const load = React.useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await API.PhoneReviews.getById(Number(id));
      setDetails(res);

      setSupplierReply(res.reviewPhoneReview?.supplierReplyText ?? "");
      setHandledComment(res.reviewPhoneReview?.handledComment ?? "");
    } finally {
      setLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    load();
  }, [load]);

  const addAttempt = async () => {
    if (!id) return;
    if (!newAttemptContactName.trim()) return;
    if (!newAttemptAt) return;

    await API.PhoneReviews.addAttempt(Number(id), {
      contactName: newAttemptContactName.trim(),
      attemptAt: newAttemptAt,
      notes: newAttemptNotes?.trim() ? newAttemptNotes.trim() : null,
      outcome: newAttemptOutcome === "" ? null : Number(newAttemptOutcome),
    });

    setNewAttemptContactName("");
    setNewAttemptNotes("");
    setNewAttemptOutcome("");
    setNewAttemptAt(toDateTimeLocalNow());

    await load();
  };

  const saveReply = async () => {
    if (!id) return;
    if (!supplierReply.trim()) return;

    await API.PhoneReviews.addReply(Number(id), {
      text: supplierReply.trim(),
    });

    await load();
  };

  const handleReview = async () => {
    if (!id) return;
    if (!handledComment.trim()) return;

    await API.PhoneReviews.handle(Number(id), {
      comment: handledComment.trim(),
    });

    await load();
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (!details?.reviewPhoneReview) {
    return <div style={{ padding: 16 }}>Ingen telefongranskning hittades.</div>;
  }

  const review = details.reviewPhoneReview;
  const attempts = details.attempts ?? [];
  const logs = details.logs ?? [];

  return (
    <div className="nr-review-page">
      <div className="nr-review-top">
        <Stepper status={review.status} />
      </div>

      <div className="nr-review-card">
        <div className="nr-review-followup">
          <div className="nr-review-section">
            <div className="nr-review-section-head">
              <h1 className="nr-review-section-title">Översikt</h1>
              <span className="nr-review-badge">{statusLabel(review.status)}</span>
            </div>

            <div className="nr-review-grid">
              <div>
                <div className="nr-review-label">Leverantör</div>
                <div>{review.supplierName} ({review.supplierOrgNr})</div>
              </div>

              <div>
                <div className="nr-review-label">Kund</div>
                <div>{review.customerName ?? "Okänd kund"} ({review.customerOrgNr})</div>
              </div>
            </div>

            <div className="nr-review-grid" style={{ marginTop: 12 }}>
              <div>
                <div className="nr-review-label">Kontaktperson</div>
                <div>{review.contactPerson || "—"}</div>
              </div>

              <div>
                <div className="nr-review-label">Period</div>
                <div>
                  {review.period}
                  {review.period !== review.endPeriod ? ` - ${review.endPeriod}` : ""}
                </div>
              </div>
            </div>
          </div>

          <div className="nr-review-section">
            <div className="nr-review-section-head">
              <h1 className="nr-review-section-title">Samtalsförsök</h1>
              <span className="nr-review-badge">{attempts.length} st</span>
            </div>

            <div style={{ marginBottom: 12 }}>
              {attempts.map((a: any) => (
                <div key={a.id} style={{ padding: "10px 0", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <b>{a.contactName}</b>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {getOutcomeBadge(a.outcome)}
                      <span style={{ opacity: 0.75 }}>
                        {String(a.attemptAt).replace("T", " ").slice(0, 16)}
                      </span>
                    </div>
                  </div>

                  {a.notes && <div style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>{a.notes}</div>}
                </div>
              ))}
              {attempts.length === 0 && <div style={{ opacity: 0.8 }}>Inga försök sparade ännu.</div>}
            </div>

            {review.status !== STATUS_HANDLED && (
              <>
                <div className="nr-review-grid">
                  <div>
                    <div className="nr-review-label">Ny kontakt</div>
                    <input
                      className="form-control"
                      value={newAttemptContactName}
                      onChange={(e) => setNewAttemptContactName(e.target.value)}
                      placeholder="Namn / roll"
                    />
                  </div>

                  <div>
                    <div className="nr-review-label">Datum/tid</div>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={newAttemptAt}
                      onChange={(e) => setNewAttemptAt(e.target.value)}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <div className="nr-review-label">Utfall</div>
                  <select
                    className="form-select"
                    value={newAttemptOutcome}
                    onChange={(e) => setNewAttemptOutcome(e.target.value ? Number(e.target.value) : "")}
                  >
                    <option value="">Välj…</option>
                    <option value="1">Inget svar</option>
                    <option value="2">Upptaget</option>
                    <option value="3">Svar erhållet</option>
                  </select>
                </div>

                <div style={{ marginTop: 12 }}>
                  <div className="nr-review-label">Anteckningar</div>
                  <textarea
                    className="form-control nr-review-textarea"
                    rows={4}
                    value={newAttemptNotes}
                    onChange={(e) => setNewAttemptNotes(e.target.value)}
                  />
                </div>

                <div className="nr-review-right" style={{ marginTop: 8 }}>
                  <Button
                    className="btn button-cancel"
                    onClick={addAttempt}
                    disabled={!newAttemptContactName.trim() || !newAttemptAt}
                  >
                    Spara samtalsförsök
                  </Button>
                </div>
              </>
            )}
          </div>

          <div className="nr-review-section">
            <div className="nr-review-section-head">
              <h1 className="nr-review-section-title">Leverantörens svar</h1>
              {review.status >= STATUS_REPLIED && <span className="nr-review-badge">Mottaget</span>}
            </div>

            <textarea
              className="form-control nr-review-textarea"
              rows={5}
              value={supplierReply}
              onChange={(e) => setSupplierReply(e.target.value)}
              disabled={review.status === STATUS_HANDLED}
            />

            {review.status !== STATUS_HANDLED && (
              <div className="nr-review-right">
                <Button className="btn button-cancel" onClick={saveReply} disabled={!supplierReply.trim()}>
                  Spara svar
                </Button>
              </div>
            )}
          </div>

          <div className="nr-review-closebox">
            <h1 className="nr-review-closebox-title">Slutmarkera granskning</h1>
            <div className="nr-review-closebox-hint">* Kommentar krävs för att avsluta ärendet</div>
            <textarea
              className="form-control nr-review-textarea"
              rows={3}
              value={handledComment}
              onChange={(e) => setHandledComment(e.target.value)}
              placeholder="Ange slutsats eller intern kommentar här..."
              disabled={review.status === STATUS_HANDLED}
            />
            {review.status !== STATUS_HANDLED && (
              <Button
                className="btn button-save nr-review-closebtn"
                onClick={handleReview}
                disabled={!handledComment.trim()}
              >
                Slutmarkera & Arkivera
              </Button>
            )}
          </div>

          <div className="nr-review-logs">
            <h1 className="nr-review-logs-title">Systemlogg</h1>
            <div className="nr-review-logs-list">
              {logs.map((l: any) => (
                <div key={l.id} className="nr-review-logrow">
                  <div className="nr-review-logleft">
                    <span className="nr-review-logtime">
                      {String(l.timestamp).replace("T", " ").slice(0, 19)}
                    </span>
                    <span className="nr-review-logmsg">
                      {l.message || statusLabel(l.status)}
                      {l.comment ? ` — ${l.comment}` : ""}
                    </span>
                  </div>
                  <span className="nr-review-logdot" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <InternalCommentsChatDock currentUserKey="test@mail.se" targetType="review-phone-review" targetId={Number(id)} />
    </div>
  );
};

export default FollowUpPhoneReviewPage;